/**
 * OSS 资源与媒体授权续签 Store
 *
 * 【设计原则】：
 * 1. 页面级资源缓存 (assetCache, videoAuthCache)：仅作为【当前页面】渲染期间的临时资源 URL 与视频授权缓存，
 *    在路由/页面切换或组件卸载时调用 `clearCache()` 进行重置清空，避免跨页面缓存泄漏。
 * 2. 用户头像缓存 (userAvatarCache)：单独处理与独立存储，全局持久保留，不受页面切换 `clearCache()` 的清理影响。
 */

import { create } from 'zustand';
import {
  fetchResourceAssetAccess,
  requestEmbeddedVideoPlayAuth,
} from '@/service/api/learning';
import {
  fetchRenewResourceAccessUrls,
} from '@/service/api/upload';

/** 默认 OSS 资源预热/提前续期缓冲时间（单位：秒，默认 30 分钟 = 1800 秒） */
export const DEFAULT_ASSET_PREHEAT_SECONDS = 1800;

/** 默认未下发过期时间的新资源初始保鲜时长（单位：秒，默认 30 分钟 = 1800 秒） */
export const DEFAULT_ASSET_TTL_SECONDS = DEFAULT_ASSET_PREHEAT_SECONDS;

export interface AssetAccessCacheItem {
  asset_id: string;
  url: string;
  expires_at: number;
}

export interface EmbeddedVideoAuthCacheItem {
  video_id: string;
  manifest?: string;
  play_session_id?: string;
  expires_at: number;
}

export interface UserAvatarCacheItem {
  asset_id?: string;
  url: string;
  expires_at?: number;
}

export interface AssetRenewState {
  /** 页面级图片/媒体资产访问 URL 缓存库 */
  assetCache: Record<string, AssetAccessCacheItem>;
  /** 富文本嵌入视频播放授权缓存库 */
  videoAuthCache: Record<string, EmbeddedVideoAuthCacheItem>;
  /** 当前登录用户的头像缓存 (跨页面独立保留，不受页面切换清理影响) */
  userAvatarCache: UserAvatarCacheItem | null;
  /** 全局续签 loading 状态 */
  renewing: boolean;

  /**
   * 按学习资源 ID 批量续签/刷新富文本图片访问授权
   * POST /learning/resources/{id}/asset-access
   */
  renewResourceAssets: (
    resourceId: string | number,
    assetIds: string[]
  ) => Promise<Api.Learning.ResourceAssetAccessItem[]>;

  /**
   * 申请或续期富文本嵌入视频播放授权 Manifest 及 Play Session
   * POST /learning/embedded-videos/{id}/play-auth
   */
  renewEmbeddedVideoAuth: (
    videoId: string | number
  ) => Promise<Api.Learning.PlayManifestResponse>;

  /**
   * 续期单个 OSS 资产访问地址
   * POST /assets/access-urls/renew
   */
  renewSingleAsset: (
    assetId: string | number,
    resourceType?: string,
    resourceId?: string | number
  ) => Promise<Api.Upload.RenewAccessURLResponse>;

  /**
   * 按业务资源上下文批量续签 OSS 访问地址
   * POST /assets/access-urls/renew
   */
  renewBatchResources: (
    items: Api.Upload.RenewResourceAccessURLsItemRequest[]
  ) => Promise<Api.Upload.RenewResourceAccessURLsResponse>;

  /**
   * 手动设置/更新当前用户的头像缓存
   */
  setUserAvatar: (avatar: UserAvatarCacheItem | null) => void;

  /**
   * 续发并更新当前用户的头像访问地址 (独立存储，切换页面不会被清理)
   * POST /assets/access-urls/renew
   */
  renewUserAvatar: (
    avatarAssetId: string | number,
    userId?: string | number
  ) => Promise<Api.Upload.RenewAccessURLResponse>;

  /** 手动/初始化同步单个资产访问凭证（如列表 API 初始返回的地址及过期时间） */
  setAssetCacheItem: (assetId: string, url: string, expiresAt?: number) => void;

  /** 检查某个缓存的资产是否已过期或即将过期（默认提前 30 分钟 / 1800 秒预判续期） */
  isAssetExpired: (assetId: string, preheatSeconds?: number) => boolean;

  /** 清空当前页面的资源与授权缓存 (保留用户头像缓存) */
  clearPageCache: () => void;
  /** 清空所有缓存 (包含用户头像，适用于退出登录时) */
  clearAllCache: () => void;
  /** 默认清理方法 (同 clearPageCache，切页时保护用户头像) */
  clearCache: () => void;
}

export const useAssetRenewStore = create<AssetRenewState>((set, get) => ({
  assetCache: {},
  videoAuthCache: {},
  userAvatarCache: null,
  renewing: false,

  renewResourceAssets: async (resourceId, assetIds) => {
    const validAssetIds = Array.from(new Set(assetIds.filter(Boolean)));
    if (!validAssetIds.length || !resourceId) return [];

    set({ renewing: true });
    try {
      const res = await fetchResourceAssetAccess(resourceId, validAssetIds);
      const items = res.assets || [];

      set((state) => {
        const nextAssetCache = { ...state.assetCache };
        items.forEach((item) => {
          if (item.asset_id && item.url) {
            nextAssetCache[item.asset_id] = {
              asset_id: item.asset_id,
              url: item.url,
              expires_at: item.expires_at || 0,
            };
          }
        });
        return { assetCache: nextAssetCache, renewing: false };
      });

      return items;
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  renewEmbeddedVideoAuth: async (videoId) => {
    if (!videoId) {
      throw new Error('缺失视频标识');
    }

    set({ renewing: true });
    try {
      const res = await requestEmbeddedVideoPlayAuth(videoId);
      const videoKey = String(videoId);

      set((state) => ({
        videoAuthCache: {
          ...state.videoAuthCache,
          [videoKey]: {
            video_id: videoKey,
            manifest: res.manifest,
            play_session_id: res.play_session_id,
            expires_at: res.expires_at || 0,
          },
        },
        renewing: false,
      }));

      return res;
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  renewSingleAsset: async (assetId, resourceType = 'COMMON', resourceId) => {
    const key = String(assetId);
    if (!get().isAssetExpired(key)) {
      const existing = get().assetCache[key];
      return {
        asset_id: key,
        access_url: existing?.url,
        expires_at: existing?.expires_at,
      };
    }

    set({ renewing: true });
    try {
      const res = await fetchRenewResourceAccessUrls({
        resources: [
          {
            resource_type: resourceType,
            resource_id: String(resourceId || key),
            asset_ids: [key],
          },
        ],
      });
      const successItem = res.items?.[0];
      if (successItem && successItem.access_url) {
        const itemResult = {
          asset_id: key,
          access_url: successItem.access_url,
          expires_at: successItem.expires_at || 0,
        };
        set((state) => ({
          assetCache: {
            ...state.assetCache,
            [key]: {
              asset_id: key,
              url: successItem.access_url!,
              expires_at: successItem.expires_at || 0,
            },
          },
          renewing: false,
        }));
        return itemResult;
      } else {
        set({ renewing: false });
        return { asset_id: key };
      }
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  renewBatchResources: async (items) => {
    if (!items || !items.length) {
      return { items: [], failed: [] };
    }

    // 过滤出至少包含一个已过期或即将到期资产的请求
    const pendingItems: Api.Upload.RenewResourceAccessURLsItemRequest[] = [];

    items.forEach((item) => {
      const expiredAssetIds = item.asset_ids.filter((id) => get().isAssetExpired(String(id)));
      if (expiredAssetIds.length) {
        pendingItems.push({
          ...item,
          asset_ids: expiredAssetIds,
        });
      }
    });

    // 如果所有的资产全在有效保鲜期内，拦截发包，直接返回空响应
    if (!pendingItems.length) {
      return { items: [], failed: [] };
    }

    set({ renewing: true });
    try {
      const res = await fetchRenewResourceAccessUrls({ resources: pendingItems });
      const successItems = res.items || [];

      set((state) => {
        const nextAssetCache = { ...state.assetCache };
        successItems.forEach((item) => {
          if (item.asset_id && item.access_url) {
            nextAssetCache[item.asset_id] = {
              asset_id: item.asset_id,
              url: item.access_url,
              expires_at: item.expires_at || 0,
            };
          }
        });
        return { assetCache: nextAssetCache, renewing: false };
      });

      return res;
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  setUserAvatar: (avatar) => set({ userAvatarCache: avatar }),

  renewUserAvatar: async (avatarAssetId, userId) => {
    if (!avatarAssetId) {
      throw new Error('缺失头像资产标识');
    }

    const key = String(avatarAssetId);
    set({ renewing: true });
    try {
      const res = await fetchRenewResourceAccessUrls({
        resources: [
          {
            resource_type: 'ACCOUNT',
            resource_id: String(userId || key),
            asset_ids: [key],
          },
        ],
      });
      const successItem = res.items?.[0];
      if (successItem && successItem.access_url) {
        const itemResult = {
          asset_id: key,
          access_url: successItem.access_url,
          expires_at: successItem.expires_at || 0,
        };
        set({
          userAvatarCache: {
            asset_id: key,
            url: successItem.access_url,
            expires_at: successItem.expires_at || 0,
          },
          renewing: false,
        });
        return itemResult;
      } else {
        set({ renewing: false });
        return { asset_id: key };
      }
    } catch (err) {
      set({ renewing: false });
      throw err;
    }
  },

  setAssetCacheItem: (assetId, url, expiresAt) => {
    if (!assetId || !url) return;

    const now = Math.floor(Date.now() / 1000);
    let validExpires = 0;

    if (typeof expiresAt === 'number' && expiresAt > 0) {
      let expSec = expiresAt > 1e11 ? Math.floor(expiresAt / 1000) : expiresAt;
      // 若传进来的是相对 TTL 秒数 (例如小于 1e8，如 3600、7200)，自动转换为当前绝对时间点
      if (expSec < 1e8) {
        expSec = now + expSec;
      }
      // 若解出的过期绝对时间比当前还小或过于临近，由于这是原接口刚返回的新凭证，保障至少包含 DEFAULT_ASSET_TTL_SECONDS 有效保鲜期
      validExpires = Math.max(expSec, now + DEFAULT_ASSET_TTL_SECONDS);
    } else {
      // 未下发过期时间，赋予默认有效保护期
      validExpires = now + DEFAULT_ASSET_TTL_SECONDS;
    }

    set((state) => {
      return {
        assetCache: {
          ...state.assetCache,
          [assetId]: {
            asset_id: assetId,
            url,
            expires_at: validExpires,
          },
        },
      };
    });
  },

  isAssetExpired: (assetId, preheatSeconds = DEFAULT_ASSET_PREHEAT_SECONDS) => {
    const item = get().assetCache[assetId];
    if (!item || !item.expires_at) return true;
    const now = Math.floor(Date.now() / 1000);
    const expiresAt = item.expires_at > 1e11 ? Math.floor(item.expires_at / 1000) : item.expires_at;
    return expiresAt - now <= preheatSeconds;
  },

  // 页面切换时调用此方法：仅清空页面级资源与授权，保留用户头像
  clearPageCache: () => set({ assetCache: {}, videoAuthCache: {} }),

  // 用户登出时调用此方法：清空包括用户头象在内的全部缓存
  clearAllCache: () => set({ assetCache: {}, videoAuthCache: {}, userAvatarCache: null }),

  // 默认 clearCache 同步指向 clearPageCache (保护用户头像)
  clearCache: () => set({ assetCache: {}, videoAuthCache: {} }),
}));
